import { AuthLayout } from "@/components/layouts/AuthLayout";
import { LoadingDots } from "@/components/ui/LoadingDots";
import { Logo } from "@/components/ui/Logo";
import { Heading, Text } from "@/components/ui/Typography";
import { UAEPassButton } from "@/components/ui/UAEPassButton";
import { Button } from "@/components/ui/shadcn/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/shadcn/form";
import { FormInput } from "@/components/ui/shadcn/input";
import { Link } from "@/components/ui/shadcn/link";
import { LoginErrorCode, WrongLoginCredentialsError } from "@/api/IdentityApi";
import {
  extractScreenHintFromReturnUrl,
  extractUserTypeFromReturnUrl,
} from "@/lib/identityUtils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router";
import { z } from "zod";
import { useIdentity } from "@/components/contexts/IdentityContext/UseIdentity";
import { animDelay } from "@/lib/utils";
import { UserType } from "@/types/IdentityTypes";

const errorMessageDisplays: Record<
  LoginErrorCode | "internalServerError",
  string
> = {
  [LoginErrorCode.InvalidCredentials]: "Wrong email or password",
  internalServerError: "An error occurred while signing in, Please try again",
};

const SignInSchema = z.object({
  email: z
    .string()
    .nonempty("Email is required")
    .email("Invalid email address")
    .max(256, "Email must be at most 256 characters long"),
  password: z
    .string()
    .nonempty("Password is required")
    .min(8, "Password must be at least 8 characters long")
    .max(100, "Password must be at most 100 characters long"),
});

type SignInData = z.infer<typeof SignInSchema>;

export default function SignInPage() {
  const { login, externalLogin } = useIdentity();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAgent = searchParams.get("isAgent") === "true";
  const { state } = useLocation();
  const returnUrl = searchParams.get("ReturnUrl") || window.location.origin;

  const form = useForm<SignInData>({
    defaultValues: {
      email: "",
      password: "",
    },
    resolver: zodResolver(SignInSchema),
  });

  const userTypeFromHint = extractUserTypeFromReturnUrl(
    state?.from || returnUrl,
  );

  const onSubmit = useCallback(
    async ({ email, password }: SignInData) => {
      form.clearErrors();

      try {
        const loginResponse = await login({
          email,
          password,
          returnUrl,
          preferredUserType: userTypeFromHint ?? undefined,
        });

        if (loginResponse.requiresEmailConfirmation) {
          navigate(`/verify-email?email=${email}`, {
            state: {
              ...state,
              from: returnUrl,
            },
          });
        } else {
          window.location.href = returnUrl;
        }
      } catch (error) {
        if (error instanceof WrongLoginCredentialsError) {
          form.setError("root", {
            message: errorMessageDisplays[LoginErrorCode.InvalidCredentials],
          });
        } else {
          form.setError("root", {
            message: errorMessageDisplays.internalServerError,
          });
        }
      }
    },
    [form, login, returnUrl, userTypeFromHint, navigate, state],
  );

  const handleUaePassLogin = useCallback(() => {
    const effectiveReturnUrl = returnUrl;
    externalLogin("UaePass", encodeURIComponent(effectiveReturnUrl));
  }, [externalLogin, returnUrl]);

  // IdentityServer's login page is fixed to /signin, so a `screen=signup`
  // hint (set by login(returnUrl, _, "signup")) arrives here. Hand off to
  // /signup while preserving the authorize ReturnUrl as `from`, so registration
  // continues the same OIDC round-trip and mints a token on return — mirroring
  // the "Sign up" link below.
  const screenHint = extractScreenHintFromReturnUrl(returnUrl);
  if (screenHint === "signup") {
    const signupAsAgent = isAgent || userTypeFromHint === UserType.Agent;
    return (
      <Navigate
        to={`/signup${signupAsAgent ? "?isAgent=true" : ""}`}
        replace
        state={{ ...state, from: returnUrl }}
      />
    );
  }

  return (
    <AuthLayout>
      <div className="flex flex-col items-center text-center">
        <header
          className="anim-slide-up flex flex-col items-center gap-3"
          style={animDelay(50)}
        >
          <Logo variant="A" className="mb-4 w-[144px] sm:w-[156px]" />
          <Heading
            variant="h1"
            weight="medium"
            className="text-3xl sm:text-3xl"
          >
            Welcome back
          </Heading>
          <p className="text-sm text-muted-foreground sm:text-md">
            Sign in to continue where you left off.
          </p>
        </header>

        <div className="anim-slide-up mt-6 w-full" style={animDelay(150)}>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="w-full">
              <div className="mx-auto flex w-full flex-col items-center text-left">
                <div className="flex w-full flex-col gap-4">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <FormInput
                            placeholder="Enter your email"
                            autoComplete="email"
                            type="email"
                            disabled={form.formState.isSubmitting}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Password</FormLabel>
                        <FormControl>
                          <FormInput
                            placeholder="Enter your password"
                            type="password"
                            autoComplete="current-password"
                            disabled={form.formState.isSubmitting}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <Link
                  to={`/request-password-reset${isAgent ? "?isAgent=true" : ""}`}
                  className="my-5 w-full text-left text-sm"
                >
                  Forgot your password?
                </Link>
              </div>

              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? (
                  <LoadingDots color="current" />
                ) : (
                  "Sign in"
                )}
              </Button>

              {form.formState.errors.root && (
                <div className="anim-slide-down">
                  <Text
                    variant="text3"
                    className="mt-3 text-center text-destructive"
                  >
                    {form.formState.errors.root.message}
                  </Text>
                </div>
              )}
            </form>
          </Form>

          {!isAgent && (
            <div className="anim-slide-up" style={animDelay(250)}>
              <div className="my-4 flex items-center gap-3">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs font-medium tracking-wide uppercase text-muted-foreground">
                  or
                </span>
                <div className="h-px flex-1 bg-border" />
              </div>
              <UAEPassButton
                onClick={handleUaePassLogin}
                text="Sign in with UAE PASS"
              />
            </div>
          )}
        </div>

        <div className="anim-fade-in mt-5" style={animDelay(350)}>
          <Text variant="text3">
            Don&apos;t have an account yet?{" "}
            <Link
              to={`/signup${isAgent || userTypeFromHint === UserType.Agent ? "?isAgent=true" : ""}`}
              state={{
                ...state,
                from: returnUrl,
              }}
            >
              Sign up
            </Link>
          </Text>
        </div>
      </div>
    </AuthLayout>
  );
}
